/// <reference path="../_references.ts"/>

module App.DispatchSummary {
	import Technician = App.Administrative.Inventory.Technician;
	import ToastService = App.Utils.ToastService;

	interface LocalScope extends ng.IScope {
		menu: App.User.UserMenuController;
	}

	export class DispatchSummaryController {
		static $inject: string[] = ['DispatchSummaryService', 'ToastService', '$q', '$scope', '$timeout'];

		showMdl: boolean = false;

		loading: boolean = false;
		appointmentStatuses: Status[] = [];
		technicians: Technician[] = [];
		srStatuses: Status[] = [];
		departments: Department[] = [];
		serviceRequests: ServiceRequest[] = [];

		earlyMorning: ServiceRequest[] = [];
		lateMorning: ServiceRequest[] = [];
		earlyAfternoon: ServiceRequest[] = [];
		lateAfternoon: ServiceRequest[] = [];
		afterHours: ServiceRequest[] = [];

		showCompleted: boolean = false;

		date: Date = moment().toDate();
		dayOfWeek: string = null;

		init: boolean = false;

		constructor(
			private DispatchSummaryService: DispatchSummaryService,
			private ToastService: ToastService,
			private $q: ng.IQService,
			private $scope: LocalScope,
			private $timeout: ng.ITimeoutService
		) {
			if ($scope.menu.showDispatchSummary) {
				this.doInit();
			} else {
				const watch = $scope.$watch(
					() => $scope.menu.showDispatchSummary,
					v => {
						if (v) {
							watch();
							this.doInit();
						}
					}
				);
			}
		}

		doInit(): void {
			if (this.init) {
				return;
			}

			this.init = true;

			this.DispatchSummaryService.getAllAppointmentStatuses().then((response) => {
				this.appointmentStatuses = response.data;
			});
			this.DispatchSummaryService.getAllTechnicians().then((response) => {
				this.technicians = response.data;
			});
			this.DispatchSummaryService.getAllStatuses().then((response) => {
				this.srStatuses = response.data;
			});

			this.$scope.$watch(() => {
				return this.date;
			}, (newVal, oldVal) => {
				if(this.formatDate(newVal) !== this.formatDate(oldVal) && newVal) {
					this.dayOfWeek = moment(this.date).format('dddd');
					if(!this.loading) {
						this.initializeData();
					}
				}
			});

			this.initializeData();
			this.dayOfWeek = moment(this.date).format('dddd');
		}

		public initializeData() {
			this.loading = true;

			if(this.date === null) {
				this.ToastService.showError('Invalid Date');
				this.loading = false;
				return;
			}

			this.clearData();

			this.$q.all([this.DispatchSummaryService.getDispatchSummaryList(this.date), this.DispatchSummaryService.getAllDepartments()])
				.then((data) => {
					this.serviceRequests = data[0].data;
					this.departments = data[1].data;
					this.setColors();
					this.organizeTimeBlocks(this.serviceRequests);
				}).finally(() => {
					this.loading = false;
					this.upgradeMdlInputs();
				});
		}

		public organizeTimeBlocks(serviceRequests: ServiceRequest[]) {
			const vm = this;

			angular.forEach(serviceRequests, function(sr) {
				var time = null;

				var apptsOnDate = sr.appointments.filter(function(appt: Appointment) {
					if(appt.appointment_status !== 'Cancelled Appointment' && appt.appointment_status !== 'Deleted Appointment') {
						return appt.scheduled_date === vm.formatDate(vm.date);
					}
					return false;
				});

				if(sr.appointments.length === 0) {
					time = moment(sr.promised_time, 'hh:mma');
					vm.insertSr(time, angular.copy(sr));
				} else {
					angular.forEach(apptsOnDate, function(appt) {
						time = moment(appt.scheduled_time, 'HH:mm');
						vm.insertSr(time, angular.copy(sr), appt);
					});
				}
			});
		}

		public insertSr(time: moment.Moment, sr: ServiceRequest, currentAppt: Appointment = null) {
			const vm = this;
			sr.timeOrdering = time.format('HH:mm');
			angular.forEach(vm.timeBlocks, function(block) {
				if(time.isSameOrAfter(block.start) && time.isSameOrBefore(block.end)) {
					if(currentAppt) {
						sr.apptId = currentAppt.id;
						sr.apptTime = time.format('ha').slice(0, -1);
						sr.apptStatus = currentAppt.appointment_status;
						sr.apptStatusIcon = vm.getIconName(sr.apptStatus);
						sr.chipTech = currentAppt.technician ? currentAppt.technician.full_name : 'None';
						sr.isChecked = currentAppt.appointment_status === 'Completed Appointment';
					} else {
						sr.chipTech = 'None';
						sr.isChecked = false;
					}

					sr.customerName = sr.customer.company_name && sr.customer.company_name !== '' ?
						sr.customer.company_name : sr.customer.full_name;
					sr.chipTitle = sr.customerName + ' / ' + sr.chipTech + ' / ' + (sr.service_address ? sr.service_address.city : '');

					block.getVar().push(sr);
				}
			});
		}

		public getDepartmentAbbrev(department: Department) {
			if(department.abbreviation) {
				return department.abbreviation;
			}

			var arr = department.name.split(' ');

			if(arr.length === 1) {
				return arr[0].substring(0, 2);
			} else if(arr.length >= 2) {
				return arr[0].charAt(0) + arr[1].charAt(0);
			}

			return '';
		};

		public setColors() {
			const vm = this;

			var colorList = ['#4EB6D5', '#5BB082', '#B21B00', '#BB6BD9', '#EE6240', '#C7C7C7', '#21ce99', '#5bbad5', '#2962ff', '#43a047'];

			for(var i = 0; i < vm.departments.length; i++) {
				vm.departments[i].color = colorList[i % colorList.length];
			}

			angular.forEach(vm.serviceRequests, function(sr) {
				var dept = vm.departments.find(function(dept: Department) {
					if(sr.department) {
						return dept.id === sr.department.id;
					}
					return false;
				});

				if(dept) {
					sr.departmentColor = dept.color;
				}
			});
		}

		public adjustDate(days: number) {
			this.date = moment(this.date).add(days, 'days').toDate();
		};

		public formatDate(date: Date) {
			return moment(date).format('YYYY-MM-DD');
		};

		public getIconName(status: string) {
			switch(status) {
				case 'Waiting Dispatch':
					return 'ellipsis-h';
				case 'Dispatched':
					return 'th';
				case 'Acknowledged':
					return 'envelope-open';
				case 'Arrived':
					return 'share';
				case 'Started':
					return 'play';
				case 'Paused':
					return 'pause';
				case 'Completed Appointment':
					return 'check-square';
				default:
					return '';
			}
		}

		public clearData() {
			this.serviceRequests = [];
			this.earlyMorning = [];
			this.lateMorning = [];
			this.earlyAfternoon = [];
			this.lateAfternoon = [];
			this.afterHours = [];
		}

		timeBlocks = [
			{
				name: 'earlyMorning',
				start: moment('0:00', 'H:mm'),
				end: moment('10:00', 'H:mm'),
				getVar: () => { return this.earlyMorning; }
			},
			{
				name: 'lateMorning',
				start: moment('10:01', 'H:mm'),
				end: moment('12:00', 'H:mm'),
				getVar: () => { return this.lateMorning; }
			},
			{
				name: 'earlyAfternoon',
				start: moment('12:01', 'H:mm'),
				end: moment('14:00', 'H:mm'),
				getVar: () => { return this.earlyAfternoon; }
			},
			{
				name: 'lateAfternoon',
				start: moment('14:01', 'H:mm'),
				end: moment('17:00', 'H:mm'),
				getVar: () => { return this.lateAfternoon; }
			},
			{
				name: 'afterHours',
				start: moment('17:01', 'H:mm'),
				end: moment('24:00', 'H:mm'),
				getVar: () => { return this.afterHours; }
			}
		];

		upgradeMdlInputs() {
			const vm = this;
			vm.showMdl = false;
			this.$timeout(() => {
				this.showMdl = true;
			  	this.$timeout(() => {
				   componentHandler.upgradeAllRegistered();
			   })
			});
		}
	}
}